import React, { useEffect, useState } from 'react';
import { melanges } from '../data/melanges';
import ImageLoader from '../components/ImageLoader';
import ImageChecker from '../components/ImageChecker';
import { motion } from 'framer-motion';
import '../styles/global.css';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const HomePage = () => {
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 900;
  const navigate = useNavigate();

  const scrollToMelanges = () => {
    if (typeof window === 'undefined') return;
    const section = document.getElementById('melanges');
    section?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Couleurs spécifiques par mélange pour les badges
  const getMelangeColor = (melangeId) => {
    const colors = {
      'rouge-obsidienne': '#7B201A',
      'eveil-floral': '#718CC0',
      'bois-fauve': '#183712'
    };
    return colors[melangeId] || '#C26F4B';
  };

  return (
    <>
      <Helmet>
        <title>KAZA - Mélanges d'épices artisanaux pour sublimer votre cuisine</title>
        <meta name="description" content="Découvrez les mélanges d'épices KAZA : Rouge Obsidienne, Éveil Floral, Bois Fauve. Recettes, conseils et inspiration pour une cuisine créative et savoureuse." />
        <meta property="og:title" content="KAZA - Mélanges d'épices artisanaux pour sublimer votre cuisine" />
        <meta property="og:description" content="Découvrez les mélanges d'épices KAZA : Rouge Obsidienne, Éveil Floral, Bois Fauve. Recettes, conseils et inspiration pour une cuisine créative et savoureuse." />
        <meta property="og:image" content="/images/logo-kaza-texte.svg" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://kaza-epices.com/" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="KAZA - Mélanges d'épices artisanaux pour sublimer votre cuisine" />
        <meta name="twitter:description" content="Découvrez les mélanges d'épices KAZA : Rouge Obsidienne, Éveil Floral, Bois Fauve. Recettes, conseils et inspiration pour une cuisine créative et savoureuse." />
        <meta name="twitter:image" content="/images/logo-kaza-texte.svg" />
        {/* Schema.org Organization et WebSite */}
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Organization',
          'name': 'KAZA',
          'url': 'https://kaza-epices.com/',
          'logo': 'https://kaza-epices.com/images/logo-kaza-texte.svg',
          'description': "Découvrez les mélanges d'épices artisanaux KAZA. Rouge Obsidienne, Éveil Floral, Bois Fauve - Des saveurs uniques pour sublimer vos plats.",
          'sameAs': [
            'https://www.instagram.com/kaza.epices/'
          ]
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          'name': 'KAZA',
          'url': 'https://kaza-epices.com/',
          'potentialAction': {
            '@type': 'SearchAction',
            'target': 'https://kaza-epices.com/?q={search_term_string}',
            'query-input': 'required name=search_term_string'
          }
        })}</script>
      </Helmet>
      
      {/* Hero Section */}
      <section className="hero-maison">
        <div className="container hero-grid">
          <div className="hero-content">
            <p className="hero-eyebrow">C’est la fin des préventes</p>
            <h1>
              Merci à nos 200 voyageurs d’avoir embarqué dans l’aventure Kaza
            </h1>
            <p className="hero-lede">
              Des mélanges prêts-à-cuisiner inspirés par nos souvenirs de voyage, des tablées pleines de rires et une obsession&nbsp;: rendre la cuisine intuitive, sensorielle, radicalement simple.
            </p>
            <div className="hero-actions">
              <button 
                className="btn btn-primary"
                onClick={scrollToMelanges}
              >
                Explorer les mélanges
              </button>
              <Link to="/notre-histoire" className="hero-link">Notre histoire</Link>
            </div>
          </div>
          <div className="hero-panel">
            <div className="hero-panel-inner">
              <p className="hero-panel-title">Une maison d’épices contemporaine</p>
              <ul className="hero-stats">
                <li>
                  <span>3</span>
                  mélanges signature, prêts à saupoudrer
                </li>
                <li>
                  <span>200</span>
                  voyageurs conquis par nos préventes
                </li>
                <li>
                  <span>1</span>
                  geste pour réveiller vos souvenirs gourmands
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Section Mission et Valeurs */}
      <section className="section section-toned">
        <div className="container" style={{ textAlign: 'center' }}>
          <h2>Notre mission</h2>
          <p className="section-lede" style={{ maxWidth: '800px', margin: '0 auto var(--spacing-xl)' }}>
            Créer des mélanges d’épices ultra précis, pensés pour sublimer sans se tromper et pour ancrer chaque plat dans une émotion.
          </p>
          
          <div className="grid grid-3 maison-grid" style={{ justifyContent: 'center' }}>
            <div className="card maison-card">
              <h3>Qualité artisanale</h3>
              <p>Chaque épice est sélectionnée à la main pour sa pureté, son intensité et sa traçabilité.</p>
            </div>
            
            <div className="card maison-card">
              <h3>Créativité culinaire</h3>
              <p>Des compositions justes et équilibrées qui libèrent l’imagination et simplifient le geste.</p>
            </div>
            
            <div className="card maison-card">
              <h3>Durabilité</h3>
              <p>Des matières premières sourcées de façon responsable et un conditionnement raisonné.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Section Mélanges */}
      <section id="melanges" className="section section-collection">
        <div className="container">
          <div className="text-center mb-5 section-collection-header">
            <p className="section-eyebrow">Explorer</p>
            <h2>Nos mélanges signature</h2>
            <p className="section-lede">
              Trois univers aromatiques pour magnifier un plat, un souvenir, une table entière.
            </p>
          </div>
          
          <div className="grid grid-3">
            {melanges.map((melange, idx) => (
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.1 + idx * 0.08, ease: [0.4, 0, 0.2, 1] }}
                className={`card melange-card melange-card-${melange.id}`}
                data-melange-id={melange.id}
                style={{
                  cursor: 'pointer',
                  maxWidth: isMobile ? 320 : 380,
                  margin: isMobile ? '0 auto 24px auto' : '0',
                  padding: isMobile ? '1.2rem 0.7rem' : '2rem 1.2rem',
                  boxShadow: isMobile ? '0 2px 12px rgba(0,0,0,0.08)' : '0 4px 24px 0 #0001',
                }}
                onClick={() => {
                  navigate(`/melange/${melange.id}`);
                }}
              >
                  <div style={{
                    width: '100%',
                    aspectRatio: '1',
                    borderRadius: '12px',
                    marginBottom: 'var(--spacing-md)',
                    overflow: 'hidden',
                    position: 'relative'
                  }}>
                    <ImageLoader
                      src={melange.image}
                      alt={`Mélange d'épices ${melange.nom} KAZA`}
                      fallback={
                        <div style={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          textAlign: 'center',
                          color: 'white',
                          fontSize: '1.2rem',
                          fontWeight: 'bold',
                          textShadow: '2px 2px 4px rgba(0,0,0,0.7)',
                          zIndex: 2
                        }}>
                          <div style={{ fontSize: '2.5rem', marginBottom: 'var(--spacing-xs)' }}>🧂</div>
                          <p>{melange.nom}</p>
                        </div>
                      }
                      overlay={true}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                  </div>
                  
                  <h3 className="titre-melange">{melange.nom}</h3>
                  <p>{melange.description}</p>
                  
                  <div style={{ marginBottom: 'var(--spacing-md)' }}>
                    <h4 style={{ fontSize: '1rem', marginBottom: 'var(--spacing-xs)' }}>Idéal avec :</h4>
                    {melange.idealesAvec.slice(0, 4).map((item, i) => (
                      <span key={i} className="badge" style={{ background: getMelangeColor(melange.id), color: '#fff', marginRight: 6 }}>{item}</span>
                    ))}
                    {melange.idealesAvec.length > 4 && (
                      <span className="badge" style={{ background: 'var(--color-border)', color: 'var(--color-text-light)' }}>+{melange.idealesAvec.length - 4} autres</span>
                    )}
                  </div>
                  <button
                    className="btn btn-secondary"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/melange/${melange.id}`);
                    }}
                    style={{ width: '100%' }}
                  >
                    Découvrir {melange.nom}
                  </button>
                </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Section Inspiration */}
      <section className="section section-toned">
        <div className="container">
          <div className="text-center mb-5">
            <p className="section-eyebrow">Inspiration</p>
            <h2>Recettes et rituels</h2>
            <p className="section-lede">
              Des accords salés ou sucrés pour apprivoiser les mélanges et signer vos propres créations.
            </p>
          </div>
          
          <div style={{ 
            maxWidth: '800px', 
            margin: '2rem auto',
            textAlign: 'center'
          }}>
            <Link 
              to="/idees-recettes" 
              className="card maison-card"
              style={{
                display: 'block',
                textDecoration: 'none',
                cursor: 'pointer',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 8px 32px rgba(0,0,0,0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)';
              }}
            >
              <h3>Toutes les recettes</h3>
              <p>Découvrez toutes nos recettes avec Rouge Obsidienne, Éveil Floral et Bois Fauve. Des idées pour sublimer vos plats, toutes en un seul endroit.</p>
              <button className="btn btn-primary" style={{ marginTop: '1rem' }}>
                Voir toutes les recettes
              </button>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default HomePage; 